import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";
import { X, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

/** v414.49 — read-only audit of already-paid-out store-order payouts:
 * recomputes each from its order with the corrected math and shows the delta
 * (overpaid red / underpaid emerald) so historical overpayments can be
 * identified. History itself is never modified. */
export default function PaidOutAuditDialog({ onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/settlements/paid-out-audit")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const rows = data ? (showAll ? data.rows : data.rows.filter((r) => r.delta !== 0)) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="paidout-audit-dialog">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-2xl p-5 my-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <MagnifyingGlass size={18} weight="bold" className="text-[#8a6a1c]" />
              {t("audit.title")}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">{t("audit.subtitle")}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="audit-close-x">
            <X size={20} weight="bold" />
          </button>
        </div>

        {!data ? (
          <p className="text-sm text-zinc-500 mt-4">{t("common.loading")}</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="border border-zinc-200 rounded-lg p-2.5">
                <p className="text-[11px] text-zinc-500">{t("audit.checked")}</p>
                <p className="text-lg font-bold" data-testid="audit-checked">{data.checked}</p>
              </div>
              <div className="border border-rose-200 bg-rose-50 rounded-lg p-2.5">
                <p className="text-[11px] text-rose-600">{t("audit.overpaid")}</p>
                <p className="text-lg font-bold text-rose-700" data-testid="audit-overpaid">{fmt$(data.total_overpaid)}</p>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-2.5">
                <p className="text-[11px] text-emerald-600">{t("audit.underpaid")}</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="audit-underpaid">{fmt$(data.total_underpaid)}</p>
              </div>
            </div>

            {data.mismatch_count > 0 && Object.keys(data.by_distributor).length > 0 && (
              <div className="mt-3 border border-zinc-200 rounded-lg p-3" data-testid="audit-by-distributor">
                <p className="text-xs font-semibold text-zinc-600 mb-1.5">{t("audit.by_dist")}</p>
                {Object.entries(data.by_distributor).map(([name, delta]) => (
                  <div key={name} className="flex justify-between text-sm py-0.5">
                    <span className="font-medium">{name}</span>
                    <span className={delta > 0 ? "text-rose-700 font-semibold" : "text-emerald-700 font-semibold"}>
                      {delta > 0 ? t("audit.over_by", { amt: fmt$(delta) }) : t("audit.under_by", { amt: fmt$(-delta) })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-4 mb-1.5">
              <p className="text-xs font-semibold text-zinc-600">
                {data.mismatch_count === 0
                  ? t("audit.all_good")
                  : t("audit.mismatch_count", { count: data.mismatch_count })}
              </p>
              {data.rows.length > data.mismatch_count && (
                <button onClick={() => setShowAll(!showAll)} className="text-xs text-zinc-500 underline" data-testid="audit-toggle-all">
                  {showAll ? t("audit.show_mismatches") : t("audit.show_all", { count: data.rows.length })}
                </button>
              )}
            </div>

            <div className="max-h-72 overflow-y-auto space-y-2">
              {rows.map((r) => (
                <div key={r.settlement_id}
                  className={`border rounded-lg p-2.5 ${r.delta > 0 ? "border-rose-300 bg-rose-50/50" : r.delta < 0 ? "border-emerald-300 bg-emerald-50/50" : "border-zinc-200"}`}
                  data-testid={`audit-row-${r.settlement_id}`}>
                  <div className="flex justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{r.distributor_name}</p>
                      <p className="text-[11px] text-zinc-500 font-mono truncate">{r.reference}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p>
                        <span className="text-zinc-500">{t("audit.paid")}:</span> <strong>{fmt$(r.paid_credit)}</strong>
                        <span className="text-zinc-400 mx-1">→</span>
                        <span className="text-zinc-500">{t("audit.correct")}:</span> <strong>{fmt$(r.correct_credit)}</strong>
                      </p>
                      {r.delta !== 0 && (
                        <p className={`font-bold ${r.delta > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {r.delta > 0 ? t("audit.over_by", { amt: fmt$(r.delta) }) : t("audit.under_by", { amt: fmt$(-r.delta) })}
                        </p>
                      )}
                    </div>
                  </div>
                  {(r.items || []).length > 0 && (
                    <p className="text-[11px] text-zinc-500 mt-1 truncate">
                      {r.items.map((it) => `${it.quantity}x ${it.product_name}`).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <BrutalButton variant="secondary" className="w-full mt-4" onClick={onClose} data-testid="audit-done-btn">
              {t("common.done")}
            </BrutalButton>
          </>
        )}
      </div>
    </div>
  );
}
